/** Tool definitions: Portfolio, accounts, holdings and performance. */

import type { ToolDefinition } from './types.js';

export const portfolioTools = {
  get_portfolios: {
    name: 'get_portfolios',
    access: 'read',
    description:
      'Get a list of all portfolios for the current user with basic statistics. Returns portfolio names, IDs, and whether they are the default portfolio.',
    inputSchema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  get_portfolio_holdings: {
    name: 'get_portfolio_holdings',
    access: 'read',
    description:
      'Get current holdings for a portfolio with live market prices, gains/losses, and account breakdown. Includes stocks, bonds, options, crypto, and cash positions. Returns comprehensive data including market values, cost basis, and performance metrics.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to fetch holdings for. Use get_portfolios to find available portfolio IDs.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter holdings. If omitted, returns holdings from all accounts.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_accounts: {
    name: 'get_accounts',
    access: 'read',
    description:
      'Get all brokerage accounts for a portfolio. Returns account names, types (taxable, IRA, 401k, etc.), institutions, sync status, and cash balances. Note: cashBalance represents ONLY cash/settlement funds, not total account value. Use get_portfolio_holdings for total account value including securities.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to fetch accounts for.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_cash_balances: {
    name: 'get_cash_balances',
    access: 'read',
    description:
      'Get cash and settlement fund balances across all accounts in a portfolio. Returns ONLY cash balances (money market funds, settlement accounts), NOT securities like stocks or bonds. Use this to answer questions about available cash or settlement fund balances. Use get_portfolio_holdings for complete portfolio value including securities.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to fetch cash balances for.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter cash balances. If omitted, returns cash from all accounts.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_portfolio_performance: {
    name: 'get_portfolio_performance',
    access: 'read',
    description:
      'Get comprehensive portfolio performance analysis including return calculations (Modified Dietz, simple ROI), income breakdown (dividends, interest, option premiums), realized gains/losses by term type, trading metrics (win rate, profit factor, holding periods), monthly performance breakdown, cash flows, and per-account summaries. Essential for answering questions about portfolio returns, investment performance, trading success rates, and income generation. Can filter by year for annual analysis or get all-time performance.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to analyze performance for.',
        },
        year: {
          type: 'number',
          description: 'Optional year to analyze (e.g., 2024). If omitted, returns all-time performance since first transaction.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter performance. If omitted, includes all accounts in the portfolio.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_benchmark_comparison: {
    name: 'get_benchmark_comparison',
    access: 'read',
    description:
      'Compare portfolio performance against benchmark symbols (default SPY) using flow-matched simulation. Returns time-weighted return (TWR) and money-weighted return (XIRR) for both the portfolio and each benchmark over a window, computed from daily EOD value snapshots.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to analyze. Use get_portfolios to find available portfolio IDs.',
        },
        symbols: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional benchmark symbols. Defaults to ["SPY"].',
        },
        window: {
          type: 'string',
          enum: ['mtd', 'qtd', 'ytd', '1y', 'inception'],
          description: 'Return window. Defaults to ytd.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_allocation: {
    name: 'get_allocation',
    access: 'read',
    description:
      'Get portfolio allocation breakdown showing how assets are distributed across different categories. Can analyze by asset class (stocks, bonds, cash, crypto), by account, or by individual security. Asset-class and symbol percentages are over gross long assets (long positions plus positive cash) and sum to 100; short-option buyback liability (shortOptionLiability) and negative cash (marginDebit) are reported as separate lines, with netLiquidationValue alongside. Account percentages are shares of net liquidation.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to analyze allocation for.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter allocation analysis.',
        },
        groupBy: {
          type: 'string',
          enum: ['assetClass', 'symbol', 'account'],
          description: 'How to group the allocation. Defaults to "assetClass".',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_daily_change: {
    name: 'get_daily_change',
    access: 'read',
    description:
      'Get daily change (intraday P&L) for a portfolio with breakdown by account and holding. Shows how much the portfolio value has changed today based on live market prices, including total dollar change, percentage change, and per-holding details. Useful for tracking daily portfolio performance and understanding which positions are driving gains or losses.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to calculate daily change for.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_open_options: {
    name: 'get_open_options',
    access: 'read',
    description:
      'Get all open (unexpired) options positions including calls and puts. Returns position details, current P&L, days to expiration, and position direction (long/short). Useful for tracking options strategies and managing risk.',
    inputSchema: {
      type: 'object',
      properties: {
        portfolioId: {
          type: 'string',
          description: 'Portfolio ID to fetch open options for.',
        },
        accountIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Optional array of account IDs to filter options.',
        },
      },
      required: ['portfolioId'],
    },
  },
  get_watchlist: {
    name: 'get_watchlist',
    access: 'read',
    description:
      'Get the user\'s watchlists and their items including symbols, notes, and price alerts.',
    inputSchema: {
      type: 'object',
      properties: {
        watchlistId: {
          type: 'string',
          description: 'Optional specific watchlist ID. If omitted, returns all watchlists.',
        },
      },
      required: [],
    },
  },
} satisfies Record<string, ToolDefinition>;
